import { Plus, Star, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useProductCartQueue } from "@/hooks/useCartQueue";
import { useProduct } from "@/hooks/useProductCache";
import { useDebounceUserInteraction } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { useCartStore } from "@/stores/cartStore";

const PLACEHOLDER_IMAGE = "/food-placeholder.jpg";

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  disponible: boolean;
  stock: number;
  image?: string;
  rating?: number;
  posId?: string | number;
}

interface ProductCardProps {
  product: Product;
  className?: string;
  viewMode?: "grid" | "list";
  currentPosId?: string | number;
}

export function ProductCard({
  product,
  className,
  viewMode = "grid",
  currentPosId,
}: ProductCardProps) {
  const {
    addProductToCart,

    pendingActions,
  } = useProductCartQueue();
  const { items } = useCartStore();

  // 🎯 USAR CACHE PARA PRODUCTO
  const { product: cachedProduct } = useProduct(
    product.id
  );
  const displayProduct = cachedProduct || product;

  const isOutOfStock = !displayProduct.disponible;

  // 🚫 Restringir agregar si el carrito contiene productos de otro POS
  const currentCartPosId = items.length > 0 ? items[0].posId : undefined;
  const effectiveProductPosId =
    displayProduct.posId !== undefined ? displayProduct.posId : currentPosId;
  const isDifferentPOS =
    currentCartPosId !== undefined &&
    effectiveProductPosId !== undefined &&
    currentCartPosId !== effectiveProductPosId;

  // 🎯 VERIFICAR SI ESTE PRODUCTO ESPECÍFICO ESTÁ SIENDO PROCESADO
  const isThisProductProcessing =
    pendingActions?.some(
      (action) =>
        action.type === "ADD_ITEM" &&
        action.data?.item?.id === displayProduct.id
    ) || false;

  // 🎯 DEBOUNCE PARA EVITAR MÚLTIPLES CLICS
  const debouncedAddToCart = useDebounceUserInteraction(
    async () => {
      if (!isOutOfStock && !isThisProductProcessing && !isDifferentPOS) {
        const productWithPos = {
          ...displayProduct,
          posId: effectiveProductPosId,
        };
        await addProductToCart(productWithPos);
      }
    },
    250 // 250ms de delay
  );

  const handleAddToCart = () => {
    debouncedAddToCart();
  };

  // Vista de lista - diseño horizontal compacto
  if (viewMode === "list") {
    return (
      <Card
        className={cn(
          "group overflow-hidden transition-all duration-200",
          "hover:shadow-lg hover:scale-[1.02]",
          "border-border/50 hover:border-border",
          "flex flex-row h-24", // Altura fija para lista
          className
        )}
      >
        {/* Imagen compacta */}
        <div className=" flex-shrink-0" style={{ width: '20vh', height: '20vh' }}>
          <img
            src={displayProduct.image || PLACEHOLDER_IMAGE}
            alt={displayProduct.name}
            className=" w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = PLACEHOLDER_IMAGE;
            }}
          />

          {isOutOfStock && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <Badge variant="destructive" className="text-xs">
                Sin stock
              </Badge>
            </div>
          )}

          {displayProduct.rating && (
            <div className="absolute top-1 left-1 flex items-center gap-1 bg-background/90 px-1 py-0.5 rounded-full shadow-sm">
              <Star className="h-2 w-2 fill-primary text-primary" />
              <span className="text-xs font-medium text-card-foreground">
                {displayProduct.rating}
              </span>
            </div>
          )}
        </div>

        {/* Columna 1: Información del producto */}
        <div className="flex-1 flex flex-col justify-center p-3 min-w-0">
          <h3 className="font-semibold text-sm line-clamp-1 group-hover:text-primary transition-colors mb-1">
            {displayProduct.name}
          </h3>

          <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
            {displayProduct.description}
          </p>

          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {displayProduct.category}
            </Badge>

            {displayProduct.stock > 0 && displayProduct.stock <= 5 && (
              <span className="text-xs text-destructive font-medium">
                ¡Solo {displayProduct.stock}!
              </span>
            )}
          </div>
        </div>

        {/* Columna 2: Precio y botón */}
        <div className="flex flex-col justify-center items-end p-3 pr-4 gap-2">
          <span className="text-sm font-bold text-primary">
            ${displayProduct.price.toLocaleString("es-CL")}
          </span>

          <Button
            onClick={handleAddToCart}
            disabled={isOutOfStock || isThisProductProcessing || isDifferentPOS}
            className="h-8 w-8 p-0 rounded-full hover:scale-105 transition-all duration-200"
            size="sm"
            variant={isOutOfStock ? "outline" : "default"}
          >
            {isOutOfStock ? (
              <X className="h-4 w-4 text-destructive" />
            ) : isThisProductProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>
      </Card>
    );
  }

  // Vista de grid - diseño vertical original
  return (
    <Card
      className={cn(
        "group overflow-hidden transition-all duration-200",
        "hover:shadow-lg hover:scale-[1.02]",
        "border-border/50 hover:border-border",
        " ", // asegura z bajo para superposiciones externas
        "h-full flex flex-col", // Asegura altura consistente
        className
      )}
    >
      <CardHeader className="p-0">
        <div className=" aspect-square overflow-hidden bg-muted" style={{ width: '100%', height: '30vh' }}>
          <img
            src={displayProduct.image || PLACEHOLDER_IMAGE}
            alt={displayProduct.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            loading="lazy" // Optimización para mobile
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = PLACEHOLDER_IMAGE;
            }}
          />

          {isOutOfStock && (
            <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
              <Badge variant="destructive" className="text-sm">
                Sin stock
              </Badge>
            </div>
          )}

          {product.rating && (
            <div className="absolute top-2 left-2 flex items-center gap-1 bg-background/90 px-2 py-1 rounded-full shadow-sm">
              <Star className="h-3 w-3 fill-primary text-primary" />
              <span className="text-xs font-medium text-card-foreground">
                {product.rating}
              </span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-3 sm:p-4 flex-1">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm sm:text-base line-clamp-2 group-hover:text-primary transition-colors">
              {displayProduct.name}
            </h3>
          </div>

          <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
            {displayProduct.description}
          </p>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">
              {displayProduct.category}
            </Badge>

            <span className="text-base sm:text-lg font-bold text-primary">
              ${displayProduct.price.toLocaleString("es-CL")}
            </span>
          </div>

          {displayProduct.stock > 0 && displayProduct.stock <= 5 && (
            <p className="text-xs text-destructive font-medium">
              ¡Solo quedan {displayProduct.stock}!
            </p>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-3 sm:p-4 pt-0">
        <Button
          onClick={handleAddToCart}
          disabled={isOutOfStock || isThisProductProcessing || isDifferentPOS}
          className="w-full h-10 sm:h-9 text-sm"
          size="sm"
        >
          {isThisProductProcessing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          {isOutOfStock
            ? "Sin stock"
            : isThisProductProcessing
            ? "Agregando..."
            : isDifferentPOS
            ? "Carrito del otro Punto de Venta"
            : "Agregar"}
        </Button>
      </CardFooter>
    </Card>
  );
}
